import React from 'react';
import { Autocomplete, Box, Chip, TextField } from '@mui/material';
import { normalizeTagsValue } from '../../utils/formatName';
import i18n from '../../i18n';
import CloseIcon from '@mui/icons-material/Close';

const tagPattern = /^\p{L}[\p{L}\p{N}\s\-_"'().«»„“”]{1,99}$/u;
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phonePattern = /^\+?[1-9]\d{1,14}$/;

const SPLIT_RE = /[,\n;\t\r]+/g;

export default class DialogTagsField extends React.Component {
    constructor(props) {
        super(props);
        let initialTags = [];
        try {
            initialTags = normalizeTagsValue(props.value);
        } catch (e) {
            initialTags = [];
        }
        this.state = {
            tags: initialTags,
            inputValue: '',
            highlightedTag: null,
            inputError: null
        };
        this._highlightTimer = null;
        this._errorTimer = null;
    }

    componentDidUpdate(prevProps) {
        if (prevProps.data.id !== this.props.data.id) {
            let initialTags = [];
            try {
                initialTags = normalizeTagsValue(this.props.value);
            } catch (e) {
                initialTags = [];
            }
            this.setState({
                tags: initialTags,
                inputValue: '',
                highlightedTag: null,
                inputError: null
            });
        }
    }

    componentWillUnmount() {
        if (this._highlightTimer) clearTimeout(this._highlightTimer);
        if (this._errorTimer) clearTimeout(this._errorTimer);
    }

    // --- helpers ---

    getValidationType = () => this.props.field?.validation || 'tags';

    getErrorText = (type) => {
        switch (type) {
        case 'email':
            return i18n.t('common.invalidEmail');
        case 'phone':
            return (
                i18n.t('common.invalidPhone')
            );
        case 'tags':
        default:
            return (
                i18n.t('common.invalidTag')
            );
        }
    };

    setError = (message, autoClearMs = 2500) => {
        if (this._errorTimer) clearTimeout(this._errorTimer);
        this.setState({ inputError: message || null });

        if (message && autoClearMs) {
            this._errorTimer = setTimeout(() => {
                this.setState({ inputError: null });
            }, autoClearMs);
        }
    };

    normalizeTag = (value) => {
        const type = this.getValidationType();
        const v = String(value || '').trim();
        if (!v) return '';

        if (type === 'email') return v.toLowerCase();
        return v;
    };

    validateInputValue = (inputValue) => {
        const type = this.getValidationType();
        const value = String(inputValue || '').trim();
        if (!value) return false;

        switch (type) {
        case 'phone':
            return phonePattern.test(value);
        case 'email':
            return emailPattern.test(value);
        case 'tags':
        default:
            return tagPattern.test(value);
        }
    };

    addManyTags = (rawValues) => {
        const current = this.state.tags || [];
        const currentSet = new Set(current);

        const added = [];
        const duplicates = [];
        const invalid = [];

        for (const raw of rawValues) {
            const normalized = this.normalizeTag(raw);
            if (!normalized) continue;

            if (!this.validateInputValue(normalized)) {
                invalid.push(raw);
                continue;
            }

            if (currentSet.has(normalized) || added.includes(normalized)) {
                duplicates.push(normalized);
                continue;
            }

            added.push(normalized);
            currentSet.add(normalized);
        }

        if (added.length > 0) {
            const newTags = [...current, ...added];
            this.setState({ tags: newTags });
            this.props.onChange(this.props.field.id, newTags);
        }

        return { added, duplicates, invalid };
    };

    highlightTag = (tag) => {
        if (!tag) return;
        this.setState({ highlightedTag: tag });
        if (this._highlightTimer) clearTimeout(this._highlightTimer);
        this._highlightTimer = setTimeout(
            () => this.setState({ highlightedTag: null }),
            600
        );
    };

    // --- commit single ---

    commitInputValue = () => {
        const { tags, inputValue } = this.state;

        const raw = String(inputValue || '').trim();
        if (!raw) {
            this.setError(null, 0);
            return;
        }

        const normalized = this.normalizeTag(raw);

        if (raw.match(SPLIT_RE)) {
            this.commitPastedValue(raw);
            return;
        }

        if (!this.validateInputValue(normalized)) {
            this.setError(this.getErrorText(this.getValidationType()));
            return;
        }

        if (tags.includes(normalized)) {
            this.setError(
                i18n.t('common.alreadyAdded'),
                1500
            );
            this.highlightTag(normalized);
            this.setState({ inputValue: '' });
            return;
        }

        const newTags = [...tags, normalized];
        this.setError(null, 0);
        this.setState({ tags: newTags, inputValue: '' });
        this.props.onChange(this.props.field.id, newTags);
    };

    // --- keyboard commit ---

    handleKeyDown = (event) => {
        const { inputValue } = this.state;

        const isCommitKey =
            event.key === 'Enter' ||
            event.key === ','; 

        const hasCommaInValue =
            typeof inputValue === 'string' && inputValue.includes(',');

        if (!isCommitKey && !hasCommaInValue) return;

        event.preventDefault();

        if (hasCommaInValue) {
            const first = inputValue.split(',')[0];
            this.setState({ inputValue: first }, this.commitInputValue);
        } else {
            this.commitInputValue();
        }
    };

    // --- delete tag ---

    handleDelete = (tagToDelete) => {
        const updatedTags = (this.state.tags || []).filter(
            (tag) => tag !== tagToDelete
        );
        this.setState({ tags: updatedTags });
        this.props.onChange(this.props.field.id, updatedTags);
    };

    // --- input change + live validation ---

    handleInputChange = (event, newInputValue) => {
        const value = typeof newInputValue === 'string' ? newInputValue : '';

        const trimmed = value.trim();
        if (!trimmed) {
            this.setState({ inputValue: value, inputError: null });
            return;
        }

        const maybeBatch = trimmed.match(SPLIT_RE);

        if (!maybeBatch) {
            const normalized = this.normalizeTag(trimmed);
            const ok = this.validateInputValue(normalized);
            this.setState({
                inputValue: value,
                inputError: ok
                    ? null
                    : this.getErrorText(this.getValidationType())
            });
        } else {
            this.setState({ inputValue: value, inputError: null });
        }
    };

    // --- paste handling ---

    parsePastedValues = (text) => {
        const parts = String(text || '')
            .split(SPLIT_RE)
            .map((p) => p.trim())
            .filter(Boolean);

        return parts;
    };

    commitPastedValue = (text) => {
        const values = this.parsePastedValues(text);
        if (values.length === 0) return;

        const { added, duplicates, invalid } = this.addManyTags(values);

        if (added.length === 0 && duplicates.length > 0) {
            this.highlightTag(duplicates[0]);
        }

        if (invalid.length > 0) {
            const type = this.getValidationType();
            const base = this.getErrorText(type);

            const examples = invalid.slice(0, 3).join(', ');
            const more = invalid.length > 3 ? ` (+${invalid.length - 3})` : '';

            this.setError(`${base}. Skipped: ${examples}${more}`, 4000);
        } else if (duplicates.length > 0) {
            this.setError(
                (i18n.t('common.duplicatesSkipped')) +
                    `: ${duplicates.slice(0, 3).join(', ')}${
                        duplicates.length > 3
                            ? ` (+${duplicates.length - 3})`
                            : ''
                    }`,
                2500
            );
        } else {
            this.setError(null, 0);
        }

        this.setState({ inputValue: '' });
    };

    handlePaste = (event) => {
        const clipboard = event.clipboardData || window.clipboardData;
        const text = clipboard ? clipboard.getData('text') : '';

        const parts = this.parsePastedValues(text);

        const looksLikeList =
            parts.length > 1 || (text && text.match(SPLIT_RE));

        if (!looksLikeList) return;

        event.preventDefault();
        this.commitPastedValue(text);
    };

    render() {
        const { field } = this.props;
        const { tags, inputValue, highlightedTag, inputError } = this.state;

        const translatedLabel = i18n.t(`columns.${field.label}`);
        const required = field.required;
        const requiredLabel = required ? ' *' : '';

        const defaultHelper = i18n.t('common.multiValuesHint');

        return (
            <Box mt={0.5} py={0.5}>
                <Autocomplete
                    multiple
                    freeSolo
                    clearIcon={null}
                    options={[]}
                    value={tags}
                    inputValue={inputValue}
                    onInputChange={this.handleInputChange}
                    onBlur={this.commitInputValue}
                    renderTags={(value, getTagProps) =>
                        value.map((tag, index) => (
                            <Chip
                                size="small"
                                key={`${tag}-${index}`}
                                label={tag}
                                {...getTagProps({ index })}
                                deleteIcon={
                                    <CloseIcon
                                        sx={{ width: '12px', height: '12px' }}
                                    />
                                }
                                onDelete={() => this.handleDelete(tag)}
                                sx={{
                                    transform:
                                        highlightedTag === tag
                                            ? 'scale(1.1)'
                                            : 'scale(1)',
                                    transition: 'transform 0.2s'
                                }}
                            />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            label={translatedLabel + requiredLabel}
                            onKeyDown={this.handleKeyDown}
                            onPaste={this.handlePaste}
                            error={Boolean(inputError)}
                            helperText={inputError || defaultHelper}
                            sx={{
                                '& .MuiAutocomplete-input': { fontSize: '16px' }
                            }}
                        />
                    )}
                />
            </Box>
        );
    }
}
